<template>
  <b-card>
    <div class="flex items-start">
      <b-button
        v-if="backButton"
        variant="primary"
        size="sm"
        class="mr-1 rounded-lg p-0"
        @click="$emit('back')"
      >
        <feather-icon
          size="2x"
          icon="ChevronLeftIcon"
        />
      </b-button>
      <div class="flex justify-between items-center w-full">
        <div class="text-black mb-2 text-2xl font-semibold">
          Pengajuan Packing
        </div>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-2"
    >
      <b-spinner
        small
        variant="primary"
        label="Small Spinner"
      />
      <p class="text-sm text-gray-500 mb-0">
        Mohon tunggu sebentar...
      </p>
    </div>

    <!-- System Error Display -->
    <div
      v-if="isSystemError && !isLoading"
      class="text-center py-8"
    >
      <div class="mb-4">
        <img
          src="https://storage.googleapis.com/komerce/core/icon-popup-warning.png"
          alt="Error"
          class="mx-auto mb-4"
          style="width: 80px; height: 80px;"
        >
      </div>
      <h4 class="text-lg font-semibold text-gray-800 mb-2">
        Terjadi Kesalahan Sistem
      </h4>
      <p class="text-gray-600 mb-6 max-w-md mx-auto">
        Maaf, terjadi kesalahan dalam sistem yang menghalangi proses pengajuan packing.
        Silakan coba refresh halaman atau hubungi tim support jika masalah berlanjut.
      </p>
      <div class="flex justify-center gap-3">
        <b-button
          variant="outline-primary"
          class="!flex !items-center !flex-wrap gap-[8px]"
          @click="handleRefreshPage"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
          />
          Refresh Halaman
        </b-button>
      </div>
    </div>

    <div
      v-if="!isSystemError && !isLoading"
    >
      <div
        class="space-y-3"
      >
        <packing-form-container
          v-for="(order, index) in orders"
          :key="ordersKeys[index]"
          :title="order ? order : 'Nama Gudang'"
          :is-single-warehouse="orders.length === 1"
          :is-default-collapsed="orders.length > index + 1"
        >
          <PackingForm
            :ref="`packingForm-${index}`"
            :fetcher-warehouses="fetcherWarehouses"
            :list-warehouses="listWarehouses"
            :used-warehouses="orders"
            :user-data="userData"
            :back-button="backButton"
            :packings="packings"
            :is-allowed-for-bulk-upload="isAllowedForBulkUpload"
            @selected-warehouse="e => handleSelectedWarehouse(e, index)"
            @revert-warehouse="(newValue, oldValue) => handleRevertWarehouse(newValue, oldValue, index)"
            @form-is-updated="e => handleFormIsUpdated(e, index)"
          />
          <div
            v-if="orders.length > 1"
            class="flex justify-end mt-[8px]"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              @click="handleRemoveInputOrder(index)"
            >
              Hapus Input Order
            </b-button>
          </div>
        </packing-form-container>
      </div>

      <div
        v-if="!isSystemError"
        class="mt-[16px] flex justify-end border-t-[1px] border-[#E2E2E2] pt-[16px]"
      >
        <b-button
          v-if="!isAllWarehouseSelected"
          variant="primary"
          :disabled="!isInputOrderCompleted"
          class="!flex !items-center gap-2"
          @click="handleAddOrderOnOtherWarehouse"
        >
          <span class="k-add text-[20px]" /> Pesanan dari Gudang Lain
        </b-button>
      </div>

      <packing-form-footer
        v-if="!isSystemError"
        :total-order="totalOrder"
        :total-fulfillment-fee="totalFulfillmentCost"
        :is-allow-submit="isInputOrderCompleted"
        :is-on-submit="isOnSubmit"
        :error-message="errorMessage"
        @submit-content="handleSubmitContent"
      />
    </div>

    <modal-handling-pickup-error
      :data="errorPickupList"
      @close="handleCloseModalHandlingPickupError"
    />
  </b-card>
</template>

<script>
import { isHavePacket } from '@core/mixins/kompack/packing'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import PackingForm from './components/PackingForm.vue'
import PackingFormContainer from './components/PackingFormContainer.vue'
import PackingFormFooter from './components/PackingFormFooter.vue'
import ModalHandlingPickupError from './components/ModalHandlingPickupError.vue'

export default {
  name: 'Packing',
  components: {
    PackingForm,
    PackingFormContainer,
    PackingFormFooter,
    ModalHandlingPickupError,
  },
  mixins: [isHavePacket],
  props: {
    userData: {
      type: Object,
      default: () => {},
    },
    backButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      orders: [''],
      ordersKeys: [Date.now()],
      nextOrderKey: Date.now() + 1,
      listWarehouses: {
        isLoading: false,
        isError: false,
        data: [],
      },
      totalFulfillmentFee: 0,
      isOnSubmit: false,
      errorMessage: '',
      selectedOrders: {},
      selectedOrderList: [],
      packings: {
        options: [],
        optionsObj: {},
      },
      isAllowedForBulkUpload: false,
      errorPickupList: [],
      kompackCost: null,
      isKompackCostLoading: false,
      kompackCostError: false,
      fetcherWarehouses: undefined,
    }
  },
  computed: {
    isLoading() {
      return this.isKompackCostLoading
    },
    isSystemError() {
      return this.fetcherWarehouses.error
    },
    isAllWarehouseSelected() {
      const totalWarehouses = this.listWarehouses.data.length
      return this.orders.length === totalWarehouses
    },
    isInputOrderCompleted() {
      return this.orders.every(order => this.selectedOrders[order] && this.selectedOrders[order].completed)
    },
    totalOrder() {
      return this.selectedOrderList.reduce((acc, order) => acc + order.totalOrder, 0)
    },
    totalFulfillmentCost() {
      return this.selectedOrderList.reduce((acc, order) => acc + order.fulfillmentCost, 0)
    },
  },
  created() {
    this.initializeFetcherWarehouses()
  },
  async mounted() {
    this.fetchPackingOptionsList()
    await this.fetcherWarehouses.fetchData({ reset: true })
    // this.isUserEligibleForBulkUpload()
  },
  methods: {
    /**
     * Fetches the kompack technological cost.
     * its bloking process because it used in packing form
     */
    async fetchKompackCost() {
      this.isKompackCostLoading = true
      this.kompackCostError = false
      try {
        const { data } = await this.$http_new.get('/komship/api/v1/kompack-costs/pickup')
        this.kompackCost = data.data.pickup_cost
        this.kompackCostError = false
      } catch (error) {
        console.error('Error fetching kompack cost:', error)
        this.kompackCost = 1000
        this.kompackCostError = true
        this.$toast_error({ message: 'Gagal mengambil data biaya kompack, silakan coba lagi' })
      } finally {
        this.isKompackCostLoading = false
      }
    },
    /**
     * Handles the submission of content.
     */
    async handleSubmitContent() {
      this.isOnSubmit = true

      try {
        const submitPromises = this.orders.map(async (order, i) => {
          if (this.$refs[`packingForm-${i}`][0]) {
            const submitResult = await this.$refs[`packingForm-${i}`][0].handleSubmitContent()
            if (submitResult?.success) {
              return this.handleUploadResi(submitResult.payload.outbound_id, submitResult.payload.file, i, order)
            }
            return {
              success: false,
              index: i,
              order,
              error: submitResult?.error,
              reason: submitResult?.reason,
              section: submitResult?.section,
            }
          }
          return {
            success: false, index: i, reason: 'form_not_found', section: 'submitPacking', order,
          }
        })

        const results = await Promise.all(submitPromises)
        const successCount = results.filter(r => r.success).length

        if (successCount === results.length) {
          // All forms submitted successfully
          await this.showSuccessConfirmation()
        } else {
          // Some forms failed
          const failedForms = results.filter(r => !r.success)
          this.errorPickupList = failedForms
          this.$bvModal.show('modal-handling-pickup-error')
        }
      } catch (error) {
        this.$toast_error({ message: 'Terjadi kesalahan saat mengajukan packing' })
      } finally {
        this.isOnSubmit = false
      }
    },
    /**
     * Handles the selection of warehouse.
     */
    handleSelectedWarehouse(warehouse, index) {
      this.$set(this.selectedOrders, warehouse.name, {
        warehouse,
        completed: false,
      })
      this.$set(this.orders, index, warehouse.name)
    },
    handleRevertWarehouse(warehouse, oldWarehouse, index) {
      this.$delete(this.selectedOrders, warehouse?.name)
      this.$set(this.orders, index, oldWarehouse?.name ?? '')
    },
    /**
     * Handles the addition of order on other warehouse.
     */
    handleAddOrderOnOtherWarehouse() {
      this.orders.push('')
      this.ordersKeys.push(this.nextOrderKey)
      this.nextOrderKey += 1
    },
    /**
     * Handles the removal of input order.
     */
    handleRemoveInputOrder(index) {
      const removedWarehouse = this.orders[index]
      // Remove arrays so the vnode keyed by ordersKeys[index] unmounts
      this.orders.splice(index, 1)
      this.ordersKeys.splice(index, 1)
      // Clear derived states
      if (removedWarehouse) this.$delete(this.selectedOrders, removedWarehouse)
      this.selectedOrderList.splice(index, 1)
    },
    /**
     * Handles the update of form.
     */
    handleFormIsUpdated(data, index) {
      if (!data.totalOrder || !data.fulfillmentCost || !data.warehouse) return
      this.$set(this.selectedOrderList, index, {
        totalOrder: data.totalOrder,
        fulfillmentCost: data.fulfillmentCost,
      })
      this.$set(this.selectedOrders, data.warehouse.name, {
        warehouse: data.warehouse,
        completed: data.completed,
      })
    },
    /**
     * Initializes the fetcher for warehouses.
     */
    initializeFetcherWarehouses() {
      const fnFetch = ({
        offset, limit, search,
      }) => this.$http_new.get(`/komship/api/v1/warehouses/partners/${this.userData.partner_detail.id}`, {
        params: {
          limit,
          page: offset,
          keyword: search,
        },
      })
        .then(response => {
          const { data } = response.data
          const seenIds = new Set()
          const deduplicatedData = data?.reduce((acc, warehouse) => {
            if (!warehouse) return acc

            const warehouseId = warehouse.id
            if (warehouseId !== undefined && warehouseId !== null) {
              if (seenIds.has(warehouseId)) {
                return acc
              }
              seenIds.add(warehouseId)
            }

            acc.push(warehouse)
            return acc
          }, [])

          return {
            data,
          }
        })
        .catch(() => {
          this.$toast_error({ message: 'Gagal mengambil data gudang, refresh halaman untuk mencoba lagi' })
          throw new Error('Gagal mengambil data gudang')
        })

      this.fetcherWarehouses = createPaginatedFetcher(fnFetch, 0, 100)
    },
    /**
     * Handles the upload of resi.
     */
    async handleUploadResi(outboundId, file, index, order) {
      if (!file) {
        return {
          success: false, error: 'No file provided', section: 'uploadResi', reason: 'no_file_provided', index, order,
        }
      }

      const formData = new FormData()
      formData.append('label', file)

      try {
        await this.$http_new.put(`/kompack/api/v1/outbounds/label/${outboundId}`, formData)
        return {
          success: true, section: 'uploadResi', index, order,
        }
      } catch {
        return {
          success: false,
          section: 'uploadResi',
          reason: 'failed_upload_resi',
          index,
          order,
        }
      }
    },
    /**
     * Shows the success confirmation.
     */
    async showSuccessConfirmation() {
      const confirmationPackingSuccess = await this.$swal({
        title: 'Pengajuan Packing Berhasil',
        text: 'Pengajuan packing akan segera diproses oleh mitra gudang',
        icon: 'success',
        iconHtml: '<img src="https://storage.googleapis.com/komerce/core/icon-popup-success.png">',
        showCancelButton: false,
        showConfirmButton: true,
        confirmButtonText: 'Selesai',
        customClass: {
          icon: 'border-0 mb-[16px]',
          confirmButton: 'btn btn-primary w-full',
        },
        buttonsStyling: false,
      })

      if (confirmationPackingSuccess.isConfirmed) {
        this.$router.push({
          path: '/history-pickup',
        })
      }
    },
    /**
     * Handles the pickup error.
     */
    async handlePickupError() {
      const confirmationPickupError = await this.$swal({
        title: 'Pengajuan Packing Gagal',
        text: 'Pengajuan packing gagal karena saldomu tidak mencukupi. Yuk Top-Up saldomu sekarang',
        icon: 'warning',
        iconHtml: '<img src="https://storage.googleapis.com/komerce/core/icon-popup-warning.png">',
      })

      if (confirmationPickupError.isConfirmed) {
        this.$router.push({
          path: '/keuangan/saldo',
        })
      }
    },
    /**
       * Fetches the list of packing options asynchronously.
       * @returns {Promise<void>} A promise that resolves when the packing options list is fetched.
       */
    async fetchPackingOptionsList() {
      const packingObj = {}
      await this.$http_komship.get('/v1/select-option/packing')
        .then(response => {
          const packing = response.data.data.map(({ name, rate, id }) => {
            packingObj[id] = {
              name,
              rate,
            }
            return {
              value: id,
              text: name,
              rate,
            }
          })
          this.packings.options = packing
          this.packings.optionsObj = packingObj
        }).catch(() => {
          this.$toast_error({
            message: 'Gagal load data, silahkan coba lagi',
          })
        }).finally(() => {
        })
    },
    /**
     * Checks if the user is eligible for bulk upload.
     */
    async isUserEligibleForBulkUpload() {
      const { data } = await this.$http_new.get('/kompack/api/v1/outbounds/bulk/permission')

      const { is_allowed } = data.data

      this.isAllowedForBulkUpload = is_allowed
    },
    handleCloseModalHandlingPickupError() {
      this.$bvModal.hide('modal-handling-pickup-error')
    },
    /**
     * Handles page refresh
     */
    handleRefreshPage() {
      window.location.reload()
    },
    /**
     * Handles retry operation for failed requests
     */
    async handleRetryOperation() {
      this.kompackCostError = false
      await this.fetchKompackCost()
    },
  },
}
</script>
